/**
 * Core-100 CORE-AU (owner direction 1) — "this vehicle's weight is not verified for this lift".
 *
 * The physical gate refuses a vehicle of unknown weight on a rated lift. A manager may authorise the
 * lift only by POSITIVELY verifying both facts the safety decision rests on:
 *   - the vehicle's weight, with where it came from (door-jamb label, manufacturer specification,
 *     certified scale, registration document) — a reason alone is not a verification;
 *   - the lift's rated capacity, read from the lift itself and typed in (it must match the bay record).
 * The server refuses an over-capacity weight outright (LIFT_UNSAFE); there is no override.
 * Anyone else is told to ask a manager.
 */
import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';

export const LIFT_WEIGHT_BASES = [
  { value: 'door_jamb_label', label: 'Door-jamb label (GVWR)' },
  { value: 'manufacturer_specification', label: 'Manufacturer specification' },
  { value: 'certified_scale', label: 'Certified scale' },
  { value: 'registration_document', label: 'Registration document' },
] as const;

/** Map the server's refusal codes to what the manager should do next. */
export function liftVerificationErrorMessage(message: string | undefined): string {
  const m = message ?? '';
  if (m.includes('LIFT_UNSAFE')) return 'This vehicle is heavier than this lift is rated for. It cannot be authorised — choose a lift with enough capacity.';
  if (m.includes('LIFT_VERIFICATION_CAPACITY_MISMATCH')) return 'The capacity you entered does not match this bay\'s record. Check the lift\'s rating plate; if the bay record is wrong, correct it first.';
  if (m.includes('LIFT_VERIFICATION_FORBIDDEN')) return 'Only a manager or owner of this shop can verify lift safety.';
  if (m.includes('LIFT_VERIFICATION_BASIS_REQUIRED')) return 'Choose where the vehicle weight came from.';
  if (m.includes('LIFT_VERIFICATION_WEIGHT_REQUIRED')) return 'Enter the verified vehicle weight in pounds.';
  if (m.includes('LIFT_VERIFICATION_NOT_A_LIFT')) return 'This bay has no rated lift on record.';
  return 'The verification could not be saved. Nothing was authorised.';
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ticketId: string;
  bayId: string;
  bayName?: string;
  /** Whether the viewer may verify (managers / owners / org admins). */
  canVerify: boolean;
  /** Called after a successful verification (the caller may retry the move). */
  onVerified: () => void;
}

export function LiftVerificationDialog({ open, onOpenChange, ticketId, bayId, bayName, canVerify, onVerified }: Props) {
  const [weight, setWeight] = useState('');
  const [basis, setBasis] = useState<string>('');
  const [capacity, setCapacity] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setError(null);
    const w = Number.parseInt(weight, 10);
    const c = Number.parseInt(capacity, 10);
    if (!Number.isFinite(w) || w <= 0) { setError('Enter the verified vehicle weight in pounds.'); return; }
    if (!basis) { setError('Choose where the vehicle weight came from.'); return; }
    if (!Number.isFinite(c) || c <= 0) { setError('Enter the lift\'s rated capacity from its rating plate.'); return; }
    setSaving(true);
    const { error: rpcError } = await supabase.rpc('verify_lift_safety' as never, {
      p_ticket_id: ticketId, p_bay_id: bayId, p_verified_weight_lbs: w, p_weight_basis: basis,
      p_confirmed_capacity_lbs: c, p_notes: null,
    } as never);
    setSaving(false);
    if (rpcError) { setError(liftVerificationErrorMessage(rpcError.message)); return; }
    setWeight(''); setBasis(''); setCapacity('');
    onOpenChange(false);
    onVerified();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px]" data-testid="lift-verification-dialog">
        <DialogHeader>
          <DialogTitle>Vehicle weight not verified for this lift</DialogTitle>
          <DialogDescription>
            {bayName ? `${bayName} is a rated lift. ` : 'This bay is a rated lift. '}
            OptiBay does not know this vehicle's weight, so it cannot go on the lift until a manager verifies it.
          </DialogDescription>
        </DialogHeader>
        {canVerify ? (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="lift-verify-weight">Verified vehicle weight (lbs)</Label>
              <Input id="lift-verify-weight" inputMode="numeric" value={weight} onChange={(e) => setWeight(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Where the weight came from</Label>
              <Select value={basis} onValueChange={setBasis}>
                <SelectTrigger data-testid="lift-verify-basis"><SelectValue placeholder="Choose a source" /></SelectTrigger>
                <SelectContent>
                  {LIFT_WEIGHT_BASES.map((b) => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="lift-verify-capacity">Lift rated capacity from its rating plate (lbs)</Label>
              <Input id="lift-verify-capacity" inputMode="numeric" value={capacity} onChange={(e) => setCapacity(e.target.value)} />
            </div>
            {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
          </div>
        ) : (
          <p className="text-sm" data-testid="lift-verify-ask-manager">
            Ask a manager to verify the vehicle's weight and the lift's rated capacity, or choose a bay without a lift.
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          {canVerify && <Button onClick={submit} disabled={saving}>{saving ? 'Saving…' : 'Verify and continue'}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
